export const activeExperiments = {};
const listenerMap = new Map();
const getCacheKey = (type, userId) => `${type}-${userId || 'global'}`;

/**
 * Registers a notification listener if it hasn't already been registered in cache
 * @param {Object} optimizely - The optimizely instance
 * @param {string} type - The notification type
 * @param {Function} handler - The handler function
 * @param {Object} options - Options object containing experimentIdMap, userId, and listenerConfig
 * @param {Object} options.experimentIdMap - Map of experiment IDs to configs
 * @param {String} options.userId - The user ID for listener scoping
 * @param {Object} options.listenerConfig - Configuration with callback functions
 */
export const addNotificationListener = (optimizely, type, handler, {
  experimentIdMap,
  userId,
  listenerConfig
} = {}) => {
  if (!optimizely?.notificationCenter) return null;
  const cacheKey = getCacheKey(type, userId);
  // Check if we already have a listener for this type
  const existing = listenerMap.get(cacheKey);
  if (existing?.notificationId) {
    optimizely.notificationCenter.removeNotificationListener(existing.notificationId);
  }
  handler = handler.bind(null, experimentIdMap, userId, listenerConfig);
  const notificationId = optimizely.notificationCenter.addNotificationListener(type, handler);
  listenerMap.set(cacheKey, {
    notificationId,
    handler
  });

  // The listener should remove when the the request is completed
  // Or after 1 minutes to avoid memory leaks
  setTimeout(() => {
    optimizely.notificationCenter.removeNotificationListener(notificationId);
    listenerMap.delete(cacheKey);
  }, 60000); // 1 minute

  return notificationId;
};

/**
 * Clears a specific listener for a given event type and userId to avoid duplicate listeners
 * @param {Object} optimizely - The optimizely instance
 * @param {String} type - The Event Type (NOTIFICATION_TYPES from optimizely-sdk)
 * @param {String} userId - The user ID to clear the listener for (optional)
 */
export const clearListener = (optimizely, type, userId) => {
  if (optimizely?.notificationCenter) {
    const cacheKey = getCacheKey(type, userId);
    const existing = listenerMap.get(cacheKey);
    if (existing) {
      optimizely.notificationCenter.removeNotificationListener(existing.notificationId);
      listenerMap.delete(cacheKey);
    }
  }
};

/**
 * Clears all listeners for a given optimizely instance
 * @param {Object} optimizely - The optimizely instance
 */
export const clearAllListeners = optimizely => {
  if (optimizely?.notificationCenter) {
    listenerMap.forEach(({
      notificationId
    }) => {
      optimizely.notificationCenter.removeNotificationListener(notificationId);
    });
    listenerMap.clear();
  }
};